import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../contexts/AuthContext";
import { Download, BookOpen, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import SharePopover from "../../components/SharePopover";
import DocAttachments from "../../components/DocAttachments";

// ISO date "YYYY-MM-DD" defaults: this month
const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
const todayStr = today.toISOString().slice(0, 10);

export default function AccountsDayBook() {
    const { user } = useAuth();
    const canDelete = user && (user.role === "admin" || user.role === "editor");
    const [data, setData] = useState(null);
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(todayStr);
    const [vtype, setVtype] = useState("all"); // all|sale|receipt
    const [busy, setBusy] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const params = { from_date: from || undefined, to_date: to || undefined };
            if (vtype !== "all") params.voucher_type = vtype;
            const { data } = await api.get("/accounts/daybook", { params });
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, vtype]);

    const downloadXlsx = () => {
        api.get("/accounts/daybook.xlsx", { params: { from_date: from, to_date: to }, responseType: "blob" })
            .then((res) => {
                const blob = new Blob([res.data]);
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `daybook-${from}_to_${to}.xlsx`;
                a.click();
            });
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            // Sales rows reference an invoice id; Receipt rows reference a payment id.
            const endpoint = pendingDelete.voucher_type === "Sales"
                ? `/invoices/${pendingDelete.ref_id}`
                : `/payments/${pendingDelete.ref_id}`;
            await api.delete(endpoint);
            toast.success(`${pendingDelete.voucher_type} ${pendingDelete.voucher_no} deleted (logged to activity)`);
            setPendingDelete(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not delete voucher");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div data-testid="accounts-daybook">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" />Tally-style</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Day Book</h1>
                    <p className="text-sm text-stone-600 mt-2">Every sale and receipt entered into the books, in chronological order.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button onClick={downloadXlsx} variant="outline" className="rounded-none border-stone-300" data-testid="daybook-xlsx"><Download className="w-4 h-4 mr-2" />Export to Excel</Button>
                    <SharePopover
                        text={data
                            ? `Day Book ${from} → ${to}\nDebit ${formatINR(data.total_debit)}\nCredit ${formatINR(data.total_credit)}\nNet ${formatINR(data.net)}`
                            : `Day Book ${from} → ${to}`}
                        subject={`Day Book · ${from} to ${to}`}
                        label="Share"
                        testId="daybook-share"
                    />
                </div>
            </div>

            <div className="bg-white border border-sky-100 p-4 mb-4 grid sm:grid-cols-4 gap-3 items-end">
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">From</Label>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="daybook-from" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">To</Label>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="daybook-to" />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Voucher type</Label>
                    <Tabs value={vtype} onValueChange={setVtype}>
                        <TabsList className="rounded-none bg-stone-100 p-0 h-auto">
                            <TabsTrigger value="all" className="rounded-none px-3 py-1.5 text-xs data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid="daybook-type-all">All</TabsTrigger>
                            <TabsTrigger value="sale" className="rounded-none px-3 py-1.5 text-xs data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid="daybook-type-sale">Sales</TabsTrigger>
                            <TabsTrigger value="receipt" className="rounded-none px-3 py-1.5 text-xs data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid="daybook-type-receipt">Receipts</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {data && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <Card label="Total debits (sales)" value={formatINR(data.total_debit)} cls="text-sky-700" />
                    <Card label="Total credits (receipts)" value={formatINR(data.total_credit)} cls="text-emerald-700" />
                    <Card label="Net" value={formatINR(data.net)} cls={data.net >= 0 ? "text-stone-900" : "text-red-700"} />
                </div>
            )}

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <Th>Date</Th><Th>Type</Th><Th>Voucher #</Th><Th>Party</Th>
                            <Th cls="text-right">Debit</Th><Th cls="text-right">Credit</Th>
                            <Th>Narration</Th>
                            {canDelete && <Th cls="text-right">Actions</Th>}
                        </tr>
                    </thead>
                    <tbody>
                        {busy ? (
                            <tr><td colSpan={canDelete ? 8 : 7} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : !data?.rows.length ? (
                            <tr><td colSpan={canDelete ? 8 : 7} className="p-8 text-center text-stone-500">No vouchers in this date range.</td></tr>
                        ) : data.rows.map((r) => (
                            <tr key={`${r.voucher_type}-${r.ref_id}`} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`db-row-${r.ref_id}`}>
                                <td className="p-2 text-stone-600 font-mono text-xs">{r.date}</td>
                                <td className="p-2">
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${r.voucher_type === "Sales" ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800"}`}>{r.voucher_type}</span>
                                </td>
                                <td className="p-2 font-mono text-xs">
                                    {r.voucher_type === "Sales"
                                        ? <Link to={`/employee/accounts/invoices/${r.ref_id}`} className="text-sky-700 hover:underline">{r.voucher_no}</Link>
                                        : r.voucher_no}
                                </td>
                                <td className="p-2 font-medium text-slate-900">{r.party || "—"}{r.party_type && <span className="text-[10px] tracking-widest text-stone-500 uppercase ml-2">{r.party_type}</span>}</td>
                                <td className="p-2 text-right font-mono text-sky-700">{r.debit ? formatINR(r.debit) : ""}</td>
                                <td className="p-2 text-right font-mono text-emerald-700">{r.credit ? formatINR(r.credit) : ""}</td>
                                <td className="p-2 text-xs text-stone-600 truncate max-w-xs">{r.narration}</td>
                                {canDelete && (
                                    <td className="p-2 text-right">
                                        <Button
                                            onClick={() => setPendingDelete(r)}
                                            variant="outline"
                                            size="sm"
                                            className="rounded-none border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 h-8 px-2"
                                            data-testid={`db-delete-${r.ref_id}`}
                                            title="Delete voucher"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <section className="mt-6 bg-white border border-stone-200">
                <header className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
                    <h2 className="font-serif text-lg text-stone-900">Supporting documents</h2>
                    <span className="text-[11px] text-stone-500">Receipts, courier bills, ledger printouts for this period</span>
                </header>
                <div className="p-4">
                    <DocAttachments
                        scope="daybook"
                        scopeId={`${from}__${to}`}
                        canEdit={user?.role === "admin" || user?.role === "editor" || (user?.capabilities || []).includes("accounts")}
                        testIdPrefix="daybook-att"
                    />
                </div>
            </section>

            <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
                <AlertDialogContent className="rounded-none" data-testid="db-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl text-red-700">
                            Delete {pendingDelete?.voucher_type} {pendingDelete?.voucher_no}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingDelete?.voucher_type === "Sales" ? (
                                <>
                                    Permanently removes invoice <strong>{pendingDelete?.voucher_no}</strong> for <strong>{pendingDelete?.party || "—"}</strong> ({formatINR(pendingDelete?.debit || 0)}).
                                    Linked payments are removed and the source order resets to <em>pending</em>.
                                </>
                            ) : (
                                <>
                                    Reverses the receipt of <strong>{formatINR(pendingDelete?.credit || 0)}</strong> against invoice <strong>{pendingDelete?.voucher_no}</strong>.
                                    The invoice's <em>paid amount</em> and <em>status</em> are recomputed from remaining payments.
                                </>
                            )}
                            <br /><br />
                            <span className="text-amber-700">This action is recorded in the Activity Log with your name and email.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="db-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="rounded-none bg-red-700 hover:bg-red-800 text-stone-50"
                            data-testid="db-delete-confirm"
                        >
                            {deleting ? "Deleting…" : "Delete permanently"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const Th = ({ children, cls = "" }) => <th className={`p-2 text-xs uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>;
const Card = ({ label, value, cls }) => (
    <div className="bg-white border border-sky-100 p-4">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`font-serif text-2xl mt-1 ${cls}`}>{value}</div>
    </div>
);
